/* @refresh reset */
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { EnvelopeOpenIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
  fetchCompanyAiMailboxMessage,
  fetchCompanyAiMailboxReplyDraftMessage,
  fetchUserPersonalMailAiMessage,
  fetchUserPersonalMailAiReplyDraftMessage,
  patchCompanyAiMailboxReplyDraftMessage,
  patchUserPersonalMailAiReplyDraftMessage,
} from "../../../api/microsoft";
import { buildOutlookWebMailboxUrl } from "../../../utils/microsoftOwaMail";
import { CompanyAiMailboxProcessingLogList } from "./CompanyAiMailboxProcessingLogList";
import { MailHtmlIframe } from "./MailHtmlIframe";
import { MailReplyDraftRichEditor } from "./MailReplyDraftRichEditor";
import { mailBodyFromGraphMessage } from "../../../utils/mailThread";

function formatRecipientList(list) {
  if (!Array.isArray(list) || list.length === 0) return "—";
  return list
    .map((x) => {
      if (!x?.email) return null;
      return x.name ? `${x.name} <${x.email}>` : x.email;
    })
    .filter(Boolean)
    .join(", ");
}

function MailReaderHtmlBody({ msg, title, emptyLabel }) {
  const body = mailBodyFromGraphMessage(msg);
  return (
    <MailHtmlIframe
      html={body.html}
      text={body.text || emptyLabel}
      title={title}
    />
  );
}

/**
 * In-app e-postleser: innhold + fane for behandlingslogg (Promang, ikke OWA).
 */
export function CompanyAiMailboxMessageReaderDialog({
  open,
  onClose,
  mailboxId,
  mailboxEmail,
  graphMessageId,
  previewSubject,
  /** @type {'company' | 'personal'} */
  variant = "company",
  zIndex = 85,
  initialTab = "message",
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(initialTab);
  const [msg, setMsg] = useState(null);
  const [msgErr, setMsgErr] = useState(null);
  const [msgLoading, setMsgLoading] = useState(false);
  const [draftHtml, setDraftHtml] = useState("");
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftErr, setDraftErr] = useState(null);
  const [draftDirty, setDraftDirty] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftSaveOk, setDraftSaveOk] = useState(false);
  const skipDraftDirtyRef = useRef(false);

  useEffect(() => {
    if (!open || !graphMessageId) return undefined;
    if (variant !== "personal" && !mailboxId) return undefined;
    setTab(initialTab || "message");
    setMsg(null);
    setMsgErr(null);
    setMsgLoading(true);
    setDraftHtml("");
    setDraftErr(null);
    setDraftDirty(false);
    setDraftSaveOk(false);
    setDraftSaving(false);
    let cancelled = false;
    const req =
      variant === "personal"
        ? fetchUserPersonalMailAiMessage(graphMessageId)
        : fetchCompanyAiMailboxMessage(mailboxId, graphMessageId);
    req
      .then((data) => {
        if (!cancelled) setMsg(data?.message || null);
      })
      .catch((e) => {
        if (!cancelled) {
          setMsg(null);
          setMsgErr(
            e?.response?.data?.errors?.[0] ||
              t("microsoft.ai_mailboxes.reader.load_failed")
          );
        }
      })
      .finally(() => {
        if (!cancelled) setMsgLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, variant, mailboxId, graphMessageId, initialTab, t]);

  useEffect(() => {
    if (!open || !graphMessageId || tab !== "reply_draft") return undefined;
    if (variant !== "personal" && !mailboxId) return undefined;

    setDraftLoading(true);
    setDraftErr(null);
    setDraftSaveOk(false);
    let cancelled = false;
    const req =
      variant === "personal"
        ? fetchUserPersonalMailAiReplyDraftMessage(graphMessageId)
        : fetchCompanyAiMailboxReplyDraftMessage(mailboxId, graphMessageId);
    req
      .then((data) => {
        if (cancelled) return;
        const body =
          data?.message?.body_content_type === "html"
            ? data.message.body_content || ""
            : data?.message?.body_content || data?.message?.body_preview || "";
        skipDraftDirtyRef.current = true;
        setDraftHtml(body || "");
        setDraftDirty(false);
      })
      .catch((e) => {
        if (cancelled) return;
        const st = e?.response?.status;
        if (st === 404) {
          setDraftErr("none");
        } else {
          setDraftErr(
            e?.response?.data?.errors?.[0] ||
              t("microsoft.ai_mailboxes.reader.reply_draft_load_failed")
          );
        }
        setDraftHtml("");
      })
      .finally(() => {
        if (!cancelled) setDraftLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, variant, mailboxId, graphMessageId, tab, t]);

  const onDraftChange = useCallback((html) => {
    if (skipDraftDirtyRef.current) {
      skipDraftDirtyRef.current = false;
      setDraftHtml(html);
      return;
    }
    setDraftHtml(html);
    setDraftDirty(true);
    setDraftSaveOk(false);
  }, []);

  const saveDraft = useCallback(() => {
    if (!graphMessageId || draftSaving) return;
    setDraftSaving(true);
    setDraftSaveOk(false);
    const payload = { graph_message_id: graphMessageId, body_html: draftHtml };
    const req =
      variant === "personal"
        ? patchUserPersonalMailAiReplyDraftMessage(payload)
        : patchCompanyAiMailboxReplyDraftMessage(mailboxId, payload);
    req
      .then(() => {
        setDraftDirty(false);
        setDraftSaveOk(true);
        window.setTimeout(() => setDraftSaveOk(false), 4000);
      })
      .catch((e) => {
        setDraftErr(
          e?.response?.data?.errors?.[0] || t("microsoft.ai_mailboxes.reader.reply_draft_load_failed")
        );
      })
      .finally(() => setDraftSaving(false));
  }, [graphMessageId, draftSaving, draftHtml, variant, mailboxId, t]);

  const owaUrl = buildOutlookWebMailboxUrl(mailboxEmail);

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative" style={{ zIndex }} onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto p-3 sm:p-6">
          <div className="flex min-h-full items-stretch justify-center sm:items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="flex w-full max-w-5xl flex-col rounded-xl border border-gray-200 bg-white shadow-xl sm:max-h-[90vh]">
                <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-100 px-4 py-3">
                  <div className="min-w-0">
                    <Dialog.Title className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                      <EnvelopeOpenIcon className="h-5 w-5 shrink-0 text-sky-600" aria-hidden />
                      {t("microsoft.ai_mailboxes.reader.title")}
                    </Dialog.Title>
                    <p className="mt-1 truncate text-xs text-gray-500" title={previewSubject}>
                      {msg?.subject || previewSubject || graphMessageId}
                    </p>
                    <p className="mt-0.5 text-[10px] text-gray-400">
                      {mailboxEmail ? (
                        <>
                          <span className="font-medium text-gray-600">{mailboxEmail}</span>
                          <span className="mx-1">·</span>
                        </>
                      ) : null}
                      <span className="font-mono">{graphMessageId}</span>
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                    aria-label={t("microsoft.ai_mailboxes.reader.close")}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="flex shrink-0 gap-1 border-b border-gray-100 px-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setTab("message")}
                    className={`rounded-t-md px-3 py-2 text-xs font-semibold ${
                      tab === "message"
                        ? "bg-white text-gray-900 ring-1 ring-gray-200 ring-b-0"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {t("microsoft.ai_mailboxes.reader.tab_message")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setTab("reply_draft")}
                    className={`rounded-t-md px-3 py-2 text-xs font-semibold ${
                      tab === "reply_draft"
                        ? "bg-white text-gray-900 ring-1 ring-gray-200 ring-b-0"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {t("microsoft.ai_mailboxes.reader.tab_reply_draft")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setTab("logs")}
                    className={`rounded-t-md px-3 py-2 text-xs font-semibold ${
                      tab === "logs"
                        ? "bg-white text-gray-900 ring-1 ring-gray-200 ring-b-0"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {t("microsoft.ai_mailboxes.reader.tab_logs")}
                  </button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
                  {tab === "reply_draft" ? (
                    <div className="space-y-3">
                      <p className="text-[11px] text-gray-600">{t("microsoft.ai_mailboxes.reader.reply_draft_hint")}</p>
                      {draftLoading ? (
                        <p className="text-sm text-gray-500">{t("microsoft.status.loading")}</p>
                      ) : draftErr === "none" ? (
                        <p className="text-sm text-gray-600">{t("microsoft.ai_mailboxes.reader.reply_draft_none")}</p>
                      ) : draftErr ? (
                        <p className="text-sm text-rose-600">{draftErr}</p>
                      ) : (
                        <>
                          <MailReplyDraftRichEditor
                            value={draftHtml}
                            onChange={onDraftChange}
                            placeholder={t("microsoft.ai_mailboxes.reader.tab_reply_draft")}
                            disabled={draftSaving}
                          />
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              disabled={draftSaving || !draftDirty}
                              onClick={saveDraft}
                              className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                            >
                              {draftSaving
                                ? t("microsoft.ai_mailboxes.reader.reply_draft_saving")
                                : t("microsoft.ai_mailboxes.reader.reply_draft_save")}
                            </button>
                            {draftSaveOk ? (
                              <span className="text-xs font-medium text-emerald-700">
                                {t("microsoft.ai_mailboxes.reader.reply_draft_saved")}
                              </span>
                            ) : null}
                          </div>
                        </>
                      )}
                    </div>
                  ) : tab === "message" ? (
                    <>
                      {msgLoading ? (
                        <p className="text-sm text-gray-500">{t("microsoft.status.loading")}</p>
                      ) : msgErr ? (
                        <p className="text-sm text-rose-600">{msgErr}</p>
                      ) : msg ? (
                        <div className="space-y-3 text-xs text-gray-800">
                          <dl className="grid gap-1 sm:grid-cols-[100px_1fr]">
                            <dt className="font-medium text-gray-500">
                              {t("microsoft.ai_mailboxes.reader.field_from")}
                            </dt>
                            <dd className="break-words">
                              {msg.from_name
                                ? `${msg.from_name} <${msg.from_email || ""}>`
                                : msg.from_email || "—"}
                            </dd>
                            <dt className="font-medium text-gray-500">
                              {t("microsoft.ai_mailboxes.reader.field_to")}
                            </dt>
                            <dd className="break-words">{formatRecipientList(msg.to)}</dd>
                            <dt className="font-medium text-gray-500">
                              {t("microsoft.ai_mailboxes.reader.field_cc")}
                            </dt>
                            <dd className="break-words">{formatRecipientList(msg.cc)}</dd>
                            <dt className="font-medium text-gray-500">
                              {t("microsoft.ai_mailboxes.reader.field_date")}
                            </dt>
                            <dd>
                              {msg.received_at
                                ? new Date(msg.received_at).toLocaleString()
                                : "—"}
                            </dd>
                          </dl>
                          {msg.has_attachments ? (
                            <p className="rounded-md bg-amber-50 px-2 py-1.5 text-amber-900 ring-1 ring-amber-100">
                              {t("microsoft.ai_mailboxes.reader.attachments_notice")}
                            </p>
                          ) : null}
                          <MailReaderHtmlBody
                            msg={msg}
                            title={t("microsoft.ai_mailboxes.reader.body_iframe_title")}
                            emptyLabel={t("microsoft.ai_mailboxes.reader.no_body")}
                          />
                        </div>
                      ) : (
                        <p className="text-sm text-gray-600">
                          {t("microsoft.ai_mailboxes.reader.no_body")}
                        </p>
                      )}
                    </>
                  ) : tab === "logs" ? (
                    <CompanyAiMailboxProcessingLogList
                      variant={variant}
                      mailboxId={mailboxId}
                      graphMessageId={graphMessageId}
                      internetMessageId={msg?.internet_message_id}
                    />
                  ) : null}
                </div>

                <div className="shrink-0 border-t border-gray-100 px-4 py-2 text-[11px] text-gray-500">
                  <p>{t("microsoft.ai_mailboxes.reader.folder_hint")}</p>
                  {owaUrl ? (
                    <p className="mt-1">
                      <a
                        href={owaUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-indigo-700 underline hover:text-indigo-900"
                      >
                        {t("microsoft.ai_mailboxes.reader.open_in_owa")}
                      </a>
                    </p>
                  ) : null}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
